import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, MapPin, GraduationCap, Plane } from "lucide-react";
import { LogoWordmark, SparkleIcon, LipstickIcon, CombIcon } from "../components/BrandIcons";
import { useContent } from "../context/ContentContext";

const ICONS = { GraduationCap, Award, MapPin, Plane };

export default function About() {
  const { content, instagram } = useContent();
  const about = content.about || {};
  const portrait = about.portrait_image || "/images/placeholders/portrait.jpg";
  const creds = about.credentials || [];

  return (
    <div data-testid="about-page">
      <section className="grain-coral border-b-2 border-ink relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-16 md:py-24 md:px-10 grid gap-10 md:grid-cols-12 items-end">
          <div className="md:col-span-7">
            <p className="eyebrow text-ink text-xs">About</p>
            <h1 className="heading mt-3 text-4xl md:text-6xl text-ink">{about.headline || "Oh hey, you found me. Lucky you."}</h1>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <div className="grain-pink card-brut p-6 relative overflow-hidden">
              <LogoWordmark size="text-5xl md:text-6xl" />
              <div className="absolute right-2 top-2 text-ink"><SparkleIcon /></div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream border-b-2 border-ink">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-24 md:px-10 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="card-brut overflow-hidden">
              <img src={portrait} alt="Rachel Morrison portrait" className="w-full h-auto block" />
            </div>
            <div className="mt-6 grain-pink card-brut p-6 relative overflow-hidden flex gap-6 items-center">
              <LipstickIcon /><CombIcon /><p className="eyebrow text-ink text-xs">{about.tools_caption || "Tools of the trade"}</p>
            </div>
          </div>
          <div className="md:col-span-7 space-y-6 text-lg text-ink/90 leading-relaxed">
            {(about.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
            <Link to="/contact" className="btn-brut" data-testid="about-cta">{about.cta || "Let’s work together"} <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <section className="grain-pink relative overflow-hidden border-b-2 border-ink">
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-16 md:py-24 md:px-10">
          <p className="eyebrow text-ink text-xs">{about.credentials_eyebrow || "Credentials"}</p>
          <h2 className="heading mt-3 text-3xl md:text-5xl text-ink">{about.credentials_headline || "For the receipts."}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {creds.map((c, i) => {
              const Icon = ICONS[Object.keys(ICONS)[i % 4]];
              return (
                <div key={i} className="card-brut bg-cream p-6">
                  <Icon className="h-6 w-6 text-coral" />
                  <p className="eyebrow mt-4 text-[0.7rem] text-ink/70">{c.title}</p>
                  <p className="mt-1 heading text-xl text-ink">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {instagram.length > 0 && (
        <section className="bg-cream" data-testid="about-instagram">
          <div className="mx-auto max-w-7xl px-5 py-16 md:py-20 md:px-10">
            <p className="eyebrow text-ink/70 text-xs">{about.instagram_eyebrow || "From the feed"}</p>
            <h2 className="heading mt-3 text-3xl md:text-5xl">{about.instagram_headline || "Behind the brushes."}</h2>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
              {instagram.slice(0, 8).map((g) => (
                <a key={g.id} href={g.link || "#"} target="_blank" rel="noreferrer" className="card-brut block aspect-square overflow-hidden bg-pinkpanel">
                  <img src={g.image} alt={g.caption || "Instagram"} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
